/**
 *  @class
 *  @function ImageWithTextVideo
 *  @description Handles dynamic video loading for image-with-text sections
 */
if (!customElements.get('image-with-text-video')) {
  class ImageWithTextVideo extends HTMLElement {
    constructor() {
      super();

      // Initialize properties
      this.videoLoaded = false;
      this.observer = null;
    }

    connectedCallback() {
      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        // Load background video dynamically
        this.loadBackgroundVideo();
      }, 100);
    }

    disconnectedCallback() {
      // Clean up observer if it exists
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    /**
     * Load background video using Intersection Observer for better performance
     */
    loadBackgroundVideo() {
      // Get video data attributes
      const videoId = this.dataset.videoId;
      const videoType = this.dataset.videoType;
      const autoplay = this.dataset.autoplay === 'true';

      // If no video ID or type, exit early
      if (!videoId || !videoType) {
        console.warn('Missing video ID or type for image-with-text-video element');
        return;
      }

      // Create intersection observer to lazy load video
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.videoLoaded) {
            this.createBackgroundVideo(videoId, videoType, autoplay);
            this.videoLoaded = true;
            this.observer.unobserve(this);
          }
        });
      }, {
        rootMargin: '100px' // Load 100px before entering viewport
      });

      // Start observing
      this.observer.observe(this);
    }

    /**
 * Create background video iframe based on provider
 * @param {string} videoId - YouTube or Vimeo video ID
 * @param {string} videoType - Video provider type ('youtube' or 'vimeo')
 * @param {boolean} autoplay - Whether to autoplay the video
 */
    createBackgroundVideo(videoId, videoType, autoplay) {
      // Create iframe element
      const iframe = document.createElement('iframe');

      // Set common iframe attributes
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'autoplay; encrypted-media');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', this.dataset.title || 'Video');
      iframe.setAttribute('tabindex', '-1');
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.position = 'absolute';
      iframe.style.top = '0';
      iframe.style.left = '0';

      // Set source based on video type
      if (videoType === 'youtube') {
        const params = new URLSearchParams();
        params.set('loop', '1');
        if (autoplay) {
          params.set('autoplay', '1');
          params.set('mute', '1');
        }
        params.set('playlist', videoId);
        params.set('iv_load_policy', '3');
        params.set('disablekb', '1');
        params.set('controls', '1');
        params.set('playsinline', '1');

        iframe.src = `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
      } else if (videoType === 'vimeo') {
        const params = new URLSearchParams();
        if (autoplay) {
          params.set('background', '1');
        }

        iframe.src = `https://player.vimeo.com/video/${videoId}${params.toString() ? `?${params.toString()}` : ''}`;
      }

      // Add iframe to container
      this.appendChild(iframe);

      // Hide the placeholder image when video is loaded
      iframe.onload = () => {
        const placeholder = this.parentElement.querySelector('img');
        if (placeholder) {
          placeholder.style.opacity = '0';
          placeholder.style.transition = 'opacity 0.3s ease';
        }
      };
    }
  }

  customElements.define('image-with-text-video', ImageWithTextVideo);
}